import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ContactName } from '@uplift/api';
import { useIsMobile, useL10n } from '@uplift/hooks';
import { EditableSectionRow } from '../../../../EditableSection';
import { PhoneForm } from './PhoneForm.component';
import { LinkButton } from '../../../../Button';
import { Alert } from '../../../../Alert';
import { VerificationTag } from '../SettingsComponents';


export const usePhoneSettingsRowStyles = makeStyles()((theme) => ({
  fontWeight500: {
    fontWeight: 500
  },
  phoneTable: {
    display: 'flex',
    flexDirection: 'column',
    '& > *:not(:last-child)': {
      paddingBottom: theme.spacing(2)
    },
  },
  mobile: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gridGap: theme.spacing(2)
  }
}));

export type PhoneSettingsRowProps = {
  rowKey: string;
  showWorkContact?: boolean;
  contacts: {
    [ContactName.mobile]?: string;
    [ContactName.work]?: string;
    [ContactName.home]?: string;
  };
  mobileVerified?: boolean;
  showVerifyAlert?: boolean;
  onVerifyClick?: () => void;
};


export const PhoneSettingsRow: React.VFC<PhoneSettingsRowProps> = ({ contacts, showWorkContact, ...props }) => {
  const l10n = useL10n();
  const { classes } = usePhoneSettingsRowStyles();
  const isMobile = useIsMobile('sm');

  const alert = props.showVerifyAlert && (props.mobileVerified === false);
  const verifyMobileClick = isMobile && alert ? props.onVerifyClick : undefined;
  const phones = [
    {
      label: l10n.providerSettings.mobile,
      value: contacts[ContactName.mobile],
    },
    {
      label: l10n.providerSettings.work,
      value: contacts[ContactName.work]
    },
    {
      label: l10n.providerSettings.home,
      value: contacts[ContactName.home]
    },
  ].filter(phone => !!phone.value);

  const content = (
    <Box onClick={verifyMobileClick} className={classes.phoneTable}>
      {phones.map(({ label, value }) => (
        <Box key={label} display="flex" flexDirection="column" gap="1px">
          <Typography variant="body1" color="textSecondary">{label}</Typography>
          <Box className={classes.mobile}>
            <Typography className={classes.fontWeight500}>
              {value}
            </Typography>
            {value === contacts[ContactName.mobile] && <VerificationTag verified={props.mobileVerified ?? false} />}
          </Box>
          {value === contacts[ContactName.mobile] && alert && (
            <Box mt={1} mb={1} maxWidth="486px">
              <Alert severity="warning">
                {l10n.providerSettings.unverified} <LinkButton variant="body2" color="textPrimary" onClick={props.onVerifyClick}>
                  {l10n.providerSettings.verify}
                </LinkButton>
              </Alert>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );

  if (isMobile && alert) {
    return (
      <EditableSectionRow
        label={l10n.providerSettings.phone}
        kind="custom"
        {...props}
      >
        {content}
      </EditableSectionRow>
    );
  }

  return (
    <EditableSectionRow
      label={l10n.providerSettings.phone}
      form={<PhoneForm showWorkContact={showWorkContact} />}
      kind="editable-row"
      {...props}
    >
      {content}
    </EditableSectionRow>
  );
};
